import client from 'client'
import type {
  UpdateUserProfileEndpointUserProfilePatchMutationRequest,
  UpdateUserProfileEndpointUserProfilePatchMutationResponse,
  UpdateUserProfileEndpointUserProfilePatchQueryParams,
  UpdateUserProfileEndpointUserProfilePatch422,
} from '../../types/UpdateUserProfileEndpointUserProfilePatch.ts'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { RequestConfig, ResponseConfig, ResponseErrorConfig } from 'client'
import { useMutation } from '@tanstack/react-query'

export const updateUserProfileEndpointUserProfilePatchMutationKey = () => [{ url: '/user/profile' }] as const

export type UpdateUserProfileEndpointUserProfilePatchMutationKey = ReturnType<typeof updateUserProfileEndpointUserProfilePatchMutationKey>

/**
 * @description Updates the user profile for the current authenticated user. Used for storing user preferences and feature acknowledgements.
 * @summary Update User Profile Endpoint
 * {@link /user/profile}
 */
export async function updateUserProfileEndpointUserProfilePatch(
  data?: UpdateUserProfileEndpointUserProfilePatchMutationRequest,
  params?: UpdateUserProfileEndpointUserProfilePatchQueryParams,
  config: Partial<RequestConfig<UpdateUserProfileEndpointUserProfilePatchMutationRequest>> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    UpdateUserProfileEndpointUserProfilePatchMutationResponse,
    ResponseErrorConfig<UpdateUserProfileEndpointUserProfilePatch422>,
    UpdateUserProfileEndpointUserProfilePatchMutationRequest
  >({ method: 'PATCH', url: `/user/profile`, params, data, ...requestConfig })
  return res
}

/**
 * @description Updates the user profile for the current authenticated user. Used for storing user preferences and feature acknowledgements.
 * @summary Update User Profile Endpoint
 * {@link /user/profile}
 */
export function useUpdateUserProfileEndpointUserProfilePatch(
  options: {
    mutation?: UseMutationOptions<
      ResponseConfig<UpdateUserProfileEndpointUserProfilePatchMutationResponse>,
      ResponseErrorConfig<UpdateUserProfileEndpointUserProfilePatch422>,
      { data?: UpdateUserProfileEndpointUserProfilePatchMutationRequest; params?: UpdateUserProfileEndpointUserProfilePatchQueryParams }
    >
    client?: Partial<RequestConfig<UpdateUserProfileEndpointUserProfilePatchMutationRequest>> & { client?: typeof client }
  } = {},
) {
  const { mutation: mutationOptions, client: config = {} } = options ?? {}
  const mutationKey = mutationOptions?.mutationKey ?? updateUserProfileEndpointUserProfilePatchMutationKey()

  return useMutation<
    ResponseConfig<UpdateUserProfileEndpointUserProfilePatchMutationResponse>,
    ResponseErrorConfig<UpdateUserProfileEndpointUserProfilePatch422>,
    { data?: UpdateUserProfileEndpointUserProfilePatchMutationRequest; params?: UpdateUserProfileEndpointUserProfilePatchQueryParams }
  >({
    mutationFn: async ({ data, params }) => {
      return updateUserProfileEndpointUserProfilePatch(data, params, config)
    },
    mutationKey,
    ...mutationOptions,
  })
}